import { useEffect, useMemo, useState } from "react";
import { css, styled } from "styled-components";

import { FilterButton, InfinityLoader, ListItemBox, LoadingCards, LockedContent } from "common/components";
import { breakpoints } from "common/styles";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { TopicCustomContentSection } from "domains/customContentAndSources/components/TopicCustomContentSection";
import { applyLatestSearchCriteria } from "domains/knowledgeFeed/actions";
import { LatestKnowledgeFeedItem } from "domains/knowledgeFeed/components/LatestKnowledgeFeedItem";
import {
  createKnowledgeFeedForTopicSelector,
  getLatestFilterCount,
  getLatestSearchCriteria,
} from "domains/knowledgeFeed/selectors";
import { activateModal } from "domains/modal/actions";
import { TopicSearchBar } from "domains/topicSearchFilter/components/TopicSearchBar";
import { getLatestContentHasFilters } from "domains/topicSearchFilter/selectors";
import { getTmSelectedId, getTmSelectedOuterNode, getTmSelectedOuterNodeId } from "domains/transformationMap/selectors";
import { createKnowledgesSelector } from "entities/knowledge/selectors";
import { fetchKnowledgeFeedForTopic } from "entities/knowledgeFeed/actions";
import {
  getCanAccessLatestFeed,
  getCanAccessTopicCustomContent,
  getCanSeeSectionContent,
} from "entities/permissions/selectors";
import { createHasUnauthenticatedContentAccessSelector } from "entities/topics/selectors";

const StyledRelativeContent = styled.div`
  position: relative;
`;

const StyledRow = styled.div`
  display: flex;
`;

const StyledSearch = styled(TopicSearchBar)`
  margin: 0;
`;

const StyledFilterSearchRow = styled(StyledRow)`
  margin-bottom: 1rem;
`;

const StyledInnerContainer = styled.div<{ hasMargins: boolean; isLocked: boolean }>`
  ${({ hasMargins }) =>
    hasMargins &&
    css`
      margin: 0 1rem;

      @media ${breakpoints.medium} {
        margin: 0 2rem;
      }

      @media ${breakpoints.large} {
        margin: 0;
        margin-right: 2rem;
      }

      @media ${breakpoints.extraLarge} {
        margin-right: 3rem;
      }
    `}
  ${({ isLocked }) =>
    isLocked &&
    css`
      filter: blur(10px);
    `}
`;
const StyledCard = styled(ListItemBox)`
  padding: 1rem;
`;

type LatestKnowledgeFeedProps = { isHorizontal?: boolean; topicId: string };

const ITEM_LIMIT = 8;

export const LatestKnowledgeFeed = ({ topicId, isHorizontal }: LatestKnowledgeFeedProps) => {
  const dispatch = useDispatch();
  const unauthenticatedSectionAccess = useSelector(createHasUnauthenticatedContentAccessSelector(topicId));
  const hasAccess = useSelector(getCanAccessLatestFeed);
  const accessToCustomContent = useSelector(getCanAccessTopicCustomContent);
  const canSeeSection = useSelector(getCanSeeSectionContent);
  const hasFilters = useSelector(getLatestContentHasFilters);
  const filterCount = useSelector(getLatestFilterCount);
  const searchCriteria = useSelector(getLatestSearchCriteria);
  const selectedInnerNodeId = useSelector(getTmSelectedId);
  const selectedOuterNode = useSelector(getTmSelectedOuterNode);
  const secondaryTopicId = useSelector(getTmSelectedOuterNodeId);
  const [selectedSection, setSelectedSection] = useState<string | undefined>(undefined);
  const translate = useTranslate();

  const id = selectedInnerNodeId ?? topicId;

  const knowledgeFeedForTopic = useSelector(createKnowledgeFeedForTopicSelector(id));

  const knowledgesSelector = useMemo(
    () => createKnowledgesSelector(knowledgeFeedForTopic?.data ?? []),
    [knowledgeFeedForTopic?.data],
  );

  const knowledge = useSelector(knowledgesSelector);

  const hasNextPage = knowledgeFeedForTopic?.hasNextPage ?? false;
  const isPending = knowledgeFeedForTopic?.isFetching ?? false;
  const isLocked = !unauthenticatedSectionAccess && hasAccess && !canSeeSection;
  const INITIAL_ARTICLES_TO_LOAD = isLocked ? 4 : 14;

  useEffect(() => {
    if (id && knowledge.length === 0) {
      dispatch(
        fetchKnowledgeFeedForTopic({
          secondaryTopicId,
          id,
          limit: INITIAL_ARTICLES_TO_LOAD,
          offset: 0,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, INITIAL_ARTICLES_TO_LOAD]);

  useEffect(() => {
    if (id && selectedOuterNode?.id) {
      dispatch(
        fetchKnowledgeFeedForTopic({
          secondaryTopicId,
          id,
          limit: INITIAL_ARTICLES_TO_LOAD,
          offset: 0,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id, selectedOuterNode?.id]);

  const selectSectionHandle = (id: string) => setSelectedSection(!selectedSection ? id : undefined);

  const loadNextPage = () => {
    dispatch(
      fetchKnowledgeFeedForTopic({
        ...searchCriteria,
        secondaryTopicId,
        id,
        offset: knowledge.length,
        limit: 20,
      }),
    );
  };

  const onSearchHandle = (search: string) => dispatch(applyLatestSearchCriteria({ search }));

  const onFilterClick = () => dispatch(activateModal({ id: "TopicLatestFilter", params: { topicId: id } }));

  if (isLocked && knowledge.length === 0) {
    return accessToCustomContent ? (
      <TopicCustomContentSection
        topicId={topicId}
        selectedSectionId={selectedSection}
        selectSectionHandle={selectSectionHandle}
      />
    ) : null;
  }

  return (
    <StyledRelativeContent>
      {accessToCustomContent && (
        <TopicCustomContentSection
          topicId={topicId}
          selectedSectionId={selectedSection}
          selectSectionHandle={selectSectionHandle}
        />
      )}
      <StyledInnerContainer hasMargins={!isHorizontal} isLocked={isLocked}>
        <StyledFilterSearchRow>
          <StyledSearch
            onSearch={onSearchHandle}
            isPending={isPending}
            dataTestId="latest-search-input-field"
            defaultValue={searchCriteria.search}
          />
          {hasFilters && (
            <FilterButton onClick={onFilterClick} count={filterCount} dataTestId="latest-search-filters-button" />
          )}
        </StyledFilterSearchRow>

        <InfinityLoader
          loadNextPage={loadNextPage}
          hasNextPage={!isLocked && hasNextPage}
          isPending={isPending}
          dataTestId="latest-knowledge-feed"
        >
          {knowledge.map((article) => (
            <ListItemBox key={article.id} data-test-id={article.id}>
              <LatestKnowledgeFeedItem knowledge={article} />
            </ListItemBox>
          ))}
          {isPending && knowledge.length === 0 && (
            <LoadingCards
              cardHeight={66}
              displayDetailButton
              imageHeight={50}
              imageWidth={50}
              lines={[30, 100]}
              numberOfItems={ITEM_LIMIT}
            />
          )}
        </InfinityLoader>
        {knowledge.length === 0 && !isPending && (
          <StyledCard data-test-id="knowledge-feed-latest-no-articles-found">
            {translate("app.discover.topic.tabs.knowledge.no_data")}
          </StyledCard>
        )}
      </StyledInnerContainer>
      {isLocked && <LockedContent trackPage="Topic detail" />}
    </StyledRelativeContent>
  );
};
